import { memo, ReactNode } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

type Props = {
    /** Ghost preview of the populated view, usually `<GhostRows />`. */
    preview?: ReactNode
    /** Call to action rendered directly under the preview, usually `<EmptyStateAction />`. */
    action?: ReactNode
    /** Optional heading for page-level notices; sidebar tabs usually do without. */
    title?: ReactNode
    /** One short sentence. Keep it to a single line of intent. */
    text?: ReactNode
    maxWidth?: number
    className?: string
    sx?: SxProps<Theme>
}

/**
 * Shared layout shell for empty tabs and panes. Owns centering, spacing and
 * the type scale; callers supply the preview, action and copy for their tab.
 */
const EmptyState = ({ preview, action, title, text, maxWidth = 240, className, sx }: Props) => {
    const hasPreviewOrAction = Boolean(preview || action)
    return (
        <Stack
            className={className}
            sx={[
                {
                    width: '100%',
                    height: '100%',
                    flex: 1,
                    minHeight: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                    px: 2,
                    py: 3
                },
                ...(Array.isArray(sx) ? sx : [sx])
            ]}
        >
            {hasPreviewOrAction && (
                <Box sx={{ width: '100%', maxWidth }}>
                    {preview}
                    {action}
                </Box>
            )}
            {title && (
                <Typography variant="subtitle1" align="center" sx={{ fontWeight: 'medium', mt: hasPreviewOrAction ? 3 : 0, maxWidth }}>
                    {title}
                </Typography>
            )}
            {text && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mt: title ? 0.75 : hasPreviewOrAction ? 3 : 0, maxWidth, lineHeight: 1.55 }}
                >
                    {text}
                </Typography>
            )}
        </Stack>
    )
}

export default memo(EmptyState)
